/**
 * Tạo con dấu đỏ tròn chuẩn pháp nhân doanh nghiệp Việt Nam dưới dạng SVG / Data URL
 * Tuân thủ Nghị định 30/2020/NĐ-CP về văn thư
 */

export function generateDefaultCompanySealSvg({
  companyName = "CÔNG TY CỔ PHẦN THƯƠNG MẠI DỊCH VỤ CIS",
  taxCode = "0110948453",
  location = "Q. CẦU GIẤY - TP. HÀ NỘI",
  size = 180,
} = {}) {
  const center = size / 2;
  const outerR = center - 6;
  const innerR = outerR - 6;
  const textR = center - 22;

  // Cắt ngắn nếu tên quá dài để không bị đè chữ
  const cleanName = companyName.toUpperCase().trim();
  const cleanLoc = location.toUpperCase().trim();

  // Tạo SVG với đường path cung tròn để uốn cong text
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
  <defs>
    <!-- Cung trên cho Tên công ty -->
    <path id="topArc" d="M ${center - textR},${center} A ${textR},${textR} 0 1,1 ${center + textR},${center}" fill="none" />
    <!-- Cung dưới cho Địa danh -->
    <path id="bottomArc" d="M ${center + textR},${center} A ${textR},${textR} 0 0,1 ${center - textR},${center}" fill="none" />
    <filter id="inkRoughness">
      <feTurbulence type="fractalNoise" baseFrequency="0.04" numOctaves="3" result="noise" />
      <feDisplacementMap in="SourceGraphic" in2="noise" scale="1.5" />
    </filter>
  </defs>

  <g filter="url(#inkRoughness)" style="opacity: 0.94;">
    <!-- Vòng tròn ngoài dày -->
    <circle cx="${center}" cy="${center}" r="${outerR}" fill="none" stroke="#cf2222" stroke-width="3.5" />
    <!-- Vòng tròn trong mỏng -->
    <circle cx="${center}" cy="${center}" r="${innerR}" fill="none" stroke="#cf2222" stroke-width="1.5" />

    <!-- Tên công ty cung tròn trên -->
    <text fill="#cf2222" font-family="'Times New Roman', serif" font-weight="900" font-size="12" letter-spacing="1.5">
      <textPath href="#topArc" startOffset="50%" text-anchor="middle">
        ★ ${cleanName} ★
      </textPath>
    </text>

    <!-- Địa danh cung tròn dưới -->
    <text fill="#cf2222" font-family="'Times New Roman', serif" font-weight="bold" font-size="10.5" letter-spacing="1.2">
      <textPath href="#bottomArc" startOffset="50%" text-anchor="middle">
        ${cleanLoc}
      </textPath>
    </text>

    <!-- Khối trung tâm: Mã số thuế & Ngôi sao -->
    <g transform="translate(${center}, ${center - 2})">
      <!-- Ngôi sao nhỏ ở giữa -->
      <polygon points="0,-14 4,-4 14,-4 6,3 9,13 0,7 -9,13 -6,3 -14,-4 -4,-4" fill="#cf2222" transform="scale(0.7)" />
      
      <!-- Dòng Mã số thuế -->
      <text x="0" y="8" fill="#cf2222" font-family="'Times New Roman', serif" font-size="9" font-weight="bold" text-anchor="middle">
        M.S.D.N: ${taxCode}
      </text>
      
      <line x1="-35" y1="13" x2="35" y2="13" stroke="#cf2222" stroke-width="1" />
    </g>
  </g>
</svg>`.trim();

  let base64 = "";
  try {
    if (typeof window !== "undefined" && window.btoa) {
      base64 = window.btoa(unescape(encodeURIComponent(svg)));
    } else {
      base64 = Buffer.from(svg).toString("base64");
    }
    return `data:image/svg+xml;base64,${base64}`;
  } catch (e) {
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }
}

/**
 * Đảm bảo lấy được ảnh con dấu hợp lệ (nếu công ty có ảnh upload thì lấy ảnh đó, không thì lấy mẫu chuẩn SVG)
 */
export function getCompanySeal(company) {
  if (company?.stampImage) {
    return company.stampImage;
  }
  return null;
}

/**
 * Tự động lọc & xóa nền giấy trắng từ ảnh chụp con dấu bằng điện thoại / bản scan
 * Tự động nhận diện mực đỏ con dấu, làm trong suốt nền giấy, làm mịn viền và crop hình vuông 1:1
 */
export function extractStampFromImage(imageSrc, { autoCrop = true, threshold = 20 } = {}) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);

      const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imgData.data;
      const w = canvas.width;
      const h = canvas.height;

      let minX = w, maxX = 0, minY = h, maxY = 0;
      let redCount = 0;

      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const idx = (y * w + x) * 4;
          const r = data[idx];
          const g = data[idx + 1];
          const b = data[idx + 2];
          const a = data[idx + 3];

          if (a === 0) continue;

          const maxGB = Math.max(g, b);
          const redDiff = r - maxGB;

          // Nếu là nét mực đỏ của con dấu
          if (r > 90 && redDiff > threshold) {
            redCount++;
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;

            // Làm mượt viền nét mực đỏ và chuẩn hóa màu đỏ mực dấu
            const smoothAlpha = Math.min(255, Math.max(60, Math.round((redDiff / 70) * 255)));
            data[idx + 3] = smoothAlpha;
            data[idx] = Math.min(210, Math.max(175, r));
            data[idx + 1] = Math.min(35, Math.round(g * 0.4));
            data[idx + 2] = Math.min(35, Math.round(b * 0.4));
          } else {
            // Nền giấy trắng/xám/tối -> xóa sạch thành trong suốt
            data[idx + 3] = 0;
          }
        }
      }

      ctx.putImageData(imgData, 0, 0);

      // Nếu không có nét đỏ đủ rõ hoặc không autoCrop
      if (redCount < 50 || !autoCrop || minX >= maxX || minY >= maxY) {
        resolve(canvas.toDataURL("image/png"));
        return;
      }

      // Tự động cắt vuông cân đối quanh con dấu (crop)
      const pad = Math.max(10, Math.round(Math.max(maxX - minX, maxY - minY) * 0.05));
      const cropW = (maxX - minX) + pad * 2;
      const cropH = (maxY - minY) + pad * 2;
      const side = Math.max(cropW, cropH);

      const cropCanvas = document.createElement("canvas");
      cropCanvas.width = side;
      cropCanvas.height = side;
      const cropCtx = cropCanvas.getContext("2d");

      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;
      const startX = Math.round(centerX - side / 2);
      const startY = Math.round(centerY - side / 2);

      cropCtx.drawImage(
        canvas,
        startX, startY, side, side,
        0, 0, side, side
      );

      resolve(cropCanvas.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = imageSrc;
  });
}
